import * as React from 'react';
import PropTypes from 'prop-types';
import { getTranslate } from 'react-localize-redux';
import { connect, useDispatch } from 'react-redux';
import {
    withStyles, Typography, Button, TextField, Input,
} from '@material-ui/core';
import {
    CartesianGrid, XAxis, YAxis, Tooltip, ResponsiveContainer,
    LineChart, Line,
} from 'recharts';

import { TopActionBar } from '../../common/components';

import * as PatientStore from '../../app/store/PatientStore';
import urls from '../../app/urls';
import { datestamp, getTextfieldDateString } from '../../youwell-common/dateUtils';
import { AddIcon } from '../../common/icons';
import { DialogButton } from '../../youwell-common/components';

const styles = theme => ({
    root: {
    },
    content: {
        padding: theme.spacing(4),
    },
    title: {
        marginBottom: theme.spacing(2),
    },
    description: {
        marginBottom: theme.spacing(2),
    },
    areas: {
        borderTop: theme.borders.normal,
        paddingTop: theme.spacing(2),
    },
    problemArea: {
        margin: theme.spacing(2, 0, 4),
    },
    boxes: {
        display: 'flex',
    },
    header: {
        color: theme.colors.grey1,
        background: theme.colors.blue4,
        padding: theme.spacing(1, 2),
        marginBottom: theme.spacing(2),
    },
    itemBox: {
        flex: '1 1 33.3%',
        padding: theme.spacing(1),
    },
    inputs: {
        marginTop: theme.spacing(2),
        display: 'flex',
    },
    inputCount: {
        width: 150,
    },
    actions: {
        marginTop: theme.spacing(2),
        padding: theme.spacing(2),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
    },
    icon: {
        marginRight: theme.spacing(1),
    },
    input: {
        marginRight: theme.spacing(2),
    },
    newAreaInput: {
        marginRight: theme.spacing(2),
        width: 300,
    },
    divider: {
        borderTop: theme.borders.normal,
    },
    deleteButton: {
        color: theme.palette.error.main,
    },
});

function createChartData(observations) {
    const observationCopy = [...observations];
    observationCopy.sort((a, b) => a.timestamp.localeCompare(b.timestamp));
    return observationCopy.map(obs => ({
        date: datestamp(new Date(obs.timestamp)),
        events: obs.value,
    }));
}

const ProblemArea = ({
    classes, translate, sensor, readOnly, studentId,
}) => {
    const chartData = React.useMemo(() => createChartData(sensor.observations), [sensor.observations]);
    const [date, setDate] = React.useState(getTextfieldDateString());
    const [count, setCount] = React.useState('');

    const dispatch = useDispatch();

    const add = () => {
        const data = {
            value: count,
            sensorId: sensor.id,
            timestamp: date,
        };

        dispatch(PatientStore.actionCreators.insertObservation(data, { reducerParams: { patientId: studentId } }));
    };

    const deleteArea = () => {
        dispatch(PatientStore.actionCreators.deleteProblemArea(sensor, { reducerParams: { patientId: studentId } }));
    };

    React.useEffect(() => {
        const getData = () => {
            dispatch(PatientStore.actionCreators.getObservations(sensor.id, { reducerParams: { patientId: studentId, sensorId: sensor.id } }));
        };

        if (sensor.observations?.length < 1) {
            getData();
        }
    }, [dispatch, sensor.id, sensor.observations?.length, studentId]);


    return (
        <div className={classes.problemArea}>
            <Typography variant="subtitle1" className={classes.header}>
                {translate('progress.problemArea')}
            </Typography>
            <div className={classes.boxes}>
                <div className={classes.itemBox}>
                    <Typography variant="h6" gutterBottom>
                        {sensor.name}
                    </Typography>
                </div>

                {!readOnly && (
                    <div className={classes.itemBox}>
                        <Typography variant="subtitle1" gutterBottom>
                            {translate('student.addObservation')}
                        </Typography>
                        <div className={classes.inputs}>
                            <TextField
                                label={translate('student.observationDate')}
                                type="date"
                                value={date}
                                onChange={e => setDate(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                                className={classes.input}
                            />
                            <TextField
                                label={translate('student.observationCount')}
                                type="number"
                                value={count}
                                onChange={e => setCount(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                                className={classes.inputCount}
                            />
                        </div>
                        <div className={classes.actions}>
                            <Button variant="outlined" color="secondary" onClick={add}>
                                <AddIcon className={classes.icon} />
                                {translate('shared.add')}
                            </Button>
                        </div>
                    </div>
                )}

                <div className={classes.itemBox}>
                    <ResponsiveContainer height={200}>
                        <LineChart data={chartData}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="date" />
                            <YAxis />
                            <Line type="monotone" fill="#8884d8" dataKey="events" />
                            <Tooltip />
                        </LineChart>
                    </ResponsiveContainer>
                </div>
            </div>

            {!readOnly && (
                <div className={classes.actions}>
                    <DialogButton
                        buttonText={translate('student.deleteProblemArea')}
                        dialogTitle={translate('student.deleteProblemAreaConfirm')}
                        dialogCancelText={translate('common.cancel')}
                        dialogOkAction={deleteArea}
                        buttonProps={{
                            className: classes.deleteButton,
                        }}
                    />
                </div>
            )}
        </div>
    );
};

ProblemArea.propTypes = {
    classes: PropTypes.object.isRequired,
    translate: PropTypes.func.isRequired,
    sensor: PropTypes.object.isRequired,
    studentId: PropTypes.string.isRequired,
    readOnly: PropTypes.bool,
};

ProblemArea.defaultProps = {
    readOnly: false,
};
const Progress = ({
    classes, translate, studentData, profileData,
}) => {
    const [newSensorName, setNewSensorName] = React.useState('');
    const dispatch = useDispatch();
    const canEdit = profileData?.userAccess?.editPatients;

    if (!studentData) {
        return null;
    }

    const addArea = () => {
        const data = {
            patientId: studentData.id,
            name: newSensorName,
        };
        if (newSensorName) {
            dispatch(PatientStore.actionCreators.insertProblemArea(data, { reducerParams: { patientId: studentData.id } }));
        }
    };

    return (
        <div className={classes.root}>
            {studentData && (
                <TopActionBar
                    title={studentData.name}
                    backLink={{
                        to: `${urls.inside.student.home}/${studentData.id}`,
                        text: translate('student.backToStudent'),
                    }}
                />
            )}

            <div className={classes.content}>
                <Typography variant="h6" className={classes.title}>
                    {translate('progress.heading')}
                </Typography>
                <Typography color="textSecondary" className={classes.description}>
                    {translate('progress.description')}
                </Typography>

                <div className={classes.areas}>
                    {studentData.sensors?.length > 0 ? studentData.sensors.map(
                        sensor => (
                            <ProblemArea
                                key={sensor.id}
                                classes={classes}
                                translate={translate}
                                sensor={sensor}
                                readOnly={!canEdit}
                                studentId={studentData.id}
                            />
                        ),
                    ) : (
                        <Typography>
                            {translate('student.noProblemAreasDefined')}
                        </Typography>
                    )}
                </div>

                <div className={classes.divider} />

                {canEdit && (
                    <div className={classes.actions}>
                        <Input className={classes.newAreaInput} placeholder={translate('progress.problemDescriptionPlaceholder')} onChange={e => setNewSensorName(e.target.value)} />
                        <Button onClick={addArea} variant="contained" color="secondary">
                            {translate('progress.addNewArea')}
                        </Button>
                    </div>
                )}
            </div>
        </div>
    );
};

Progress.propTypes = {
    classes: PropTypes.object.isRequired,
    translate: PropTypes.func.isRequired,
    studentData: PropTypes.object,
    profileData: PropTypes.object,
};

Progress.defaultProps = {
    profileData: null,
    studentData: null,
};

const mapStateToProps = (state, ownProps) => ({
    translate: getTranslate(state.localize),
    profileData: state.auth.profileData,
    studentData: state.patient.details && state.patient.details[ownProps.match.params.studentId],
});

export default connect(mapStateToProps)(withStyles(styles)(Progress));
